import { ipaMisc, ipaMulti, ipaSingle } from "./ipaAlts";
import { ColumnAlign } from "./ColumnAlign";
import './ipaButtons.css';
import { ipaToFeatures } from "../../phonemes/ipa";

/** buttons for showing/inserting IPA shortcut keys & symbols */
export function IpaButtons(props: {
  onClick: (s: string) => void,
  width?: number,

  /** if true, use IPA mapping - if false, use single/multi/misc/descriptions */
  ipa?: boolean,
  /** map from typed-key to replacement symbol */
  single?: Record<string, string>,
  /** map from shift-key (like S) to a list of options to cycle between */
  multi?: Record<string, string[]>,
  /** list of symbols to provide buttons for that don't have keyboard shortcuts */
  misc?: string[],
  /** map from symbol to its description, displayed in a tooltip */
  descriptions?: (symbol: string) => string | string[],
}) {
  const singleMap = props.ipa ? ipaSingle : props.single ?? {};
  const multiMap = props.ipa ? ipaMulti : props.multi ?? {};
  const miscNames = props.ipa ? ipaMisc : props.misc ?? [];
  const singleNames = getSingleNames(singleMap);
  const multiNames = Object.getOwnPropertyNames(multiMap);
  const descriptions = props.ipa ? getIpaFeatures : props.descriptions;

  function getIpaFeatures(symbol: string): string {
    switch (symbol) {
      case '?': return 'question mark';
      case '!': return 'exclamation mark';
    }
    const features = ipaToFeatures(symbol)?.filter(f => f !== 'modifier');
    return features === undefined ? '' : features.join(', ');
  }

  return (<ColumnAlign width={props.width}>{[
    <span key={1}>
      {singleNames.map((s, i) => showOptions(s, [singleMap[s]], i, props.onClick, descriptions))}
      {singleNames.length === 0 ? undefined : <br />}
      {multiNames.map((m, i) => <span key={i}>{showOptions(m, multiMap[m], 0, props.onClick, descriptions)}{getBr(i)}</span>)}
      {miscNames.length === 0 ? undefined
        : <>
          &nbsp;&nbsp;&nbsp;
          {showOptions('', miscNames, 1, props.onClick, descriptions)}
        </>
      }
    </span>
  ]}</ColumnAlign>);
}

function showOptions(s: string, options: string[], key: number, onClick: (s: string) => void, descriptions?: (symbol: string) => string | string[]) {
  return (<span key={key}>
    <span className="ipaSection">
      {s === '' ? undefined :
        <span className="ipaLabel">
          {s}
          <span className="ipaTooltip">{getHint(s)}</span>
        </span>}
      <span className="ipaList">
        {options.map((s, i) =>
          <span onClick={(e) => onClick(s)} className="ipaButton" key={i}>
            {s}
            <KeyTooltip s={s} descriptions={descriptions} />
          </span>)}
      </span>
    </span>
    <span> </span>
  </span>
  );
  // note that `<span> <span>` is necessary for the parent to be able
  // to find spaces to wrap on, while the ipaSection doesn't wrap its contents
}

/** turn into tooltip text, could be nothing or one line or multiple lines */
function KeyTooltip(props: { s: string, descriptions?: (symbol: string) => string | string[] }) {
  const desc = props.descriptions ? props.descriptions(props.s) : '';
  if (desc === undefined || desc === '' || (Array.isArray(desc) && desc.length === 0)) {
    return <></>;
  } else if (typeof desc === 'string') {
    return <span className="ipaTooltip">{desc}</span>;
  }
  // show first few lines
  return (<span className="ipaTooltip">
    {desc.slice(0, 4).map((d, i) => <span key={i}>{i === 0 ? <></> : <br />}{d}</span>)}
  </span>);
}

function getHint(s: string): string {
  const key = 65 <= s.charCodeAt(0) && s.charCodeAt(0) <= 91 ? `shift-${s}` : s;
  return `type ${key}`;
}

/** where to break the lists in ipaMulti */
function getBr(i: number) {
  return undefined; // this lets the system wrap - uncomment next line to force breaks
  //return ipaMultiBreak.includes(i) ? <br /> : undefined;
}

function getSingleNames(single?: Record<string, string>): string[] {
  // workaround for javascript annoyance that puts numbers at the beginning
  const list = Object.getOwnPropertyNames(single);
  const letters = list.filter(s => !Number.isInteger(Number.parseInt(s)));
  const numbers = list.filter(s => Number.isInteger(Number.parseInt(s)));
  return letters.concat(numbers);
}
